import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Loader2 } from 'lucide-react';
import { extractAxiosError } from '../components/SafeError';
import AuthHeader from '../components/AuthHeader';

/**
 * WelcomePage - First-time onboarding for new users (display name step)
 */
const WelcomePage = () => {
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!displayName.trim()) {
      setError('Please enter your preferred name');
      return;
    }

    if (displayName.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.put('/user/display-name', { display_name: displayName.trim() });
      await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(extractAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-glass-bg flex items-center justify-center p-4" data-testid="welcome-page">
      <div className="w-full max-w-md card-panel p-8">
        <AuthHeader />

        <div className="text-center mb-8">
          <h2 className="font-barlow-condensed font-bold text-2xl" style={{ color: 'var(--gc-text-main)' }}>
            Welcome, Captain.
          </h2>
          <p className="text-sm mt-2" style={{ color: 'var(--gc-text-muted)' }}>
            Before we begin, what would you like me to call you?
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="displayName" className="label-text block mb-2">
              Preferred Name
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your name"
              className="input-field"
              autoFocus
              disabled={loading}
              data-testid="display-name-input"
            />
            {error && (
              <p className="mt-3 text-sm" style={{ color: 'var(--gc-warning)' }}>{error}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary flex items-center justify-center gap-2"
            data-testid="continue-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              'Continue'
            )}
          </button>
        </form>

        <p className="text-center text-xs mt-6" style={{ color: 'var(--gc-text-faint)' }}>
          You can change this later in Settings.
        </p>
      </div>
    </div>
  );
};

export default WelcomePage;
